import { Component, Vue } from 'vue-property-decorator';
import PinchZoom from 'pinch-zoom-js';

// Distance per finger (Values in px)
const SCROLL_OFFSET = 100;
const SCROLL_MOVE_DISTANCE = 40; // the zoomFactor is about SCROLL_DISTANCE/SCROLL_OFFSET, but it depends a bit on the number of steps too
const ZOOM_EVENTS = 6;
const DRAG_MOVE_DISTANCE = 150;
const VALIDATE_MOVE_RANGE: number = 500;

// Top = 1, Right = -1, Down = -1, Left = 1
export enum ScrollDirection {
    TOP, RIGHT, DOWN, LEFT, TOPLEFT, TOPRIGHT, DOWNLEFT, DOWNRIGHT
}

/**
 * Interface for coordinates of a simulated touch. It has a position and a direction to move to. This direction can be
 * 1, 0 and -1. Its just for up, down, left, right or standing
 */
interface Coordinates {
    pos: number,
    direction: 1 | 0 | -1
}

@Component
export default class ScrollMixin extends Vue {
    /**
     * Bool to check if the element is zoomed in
     */
    public isZoomedIn: boolean = false;
    /**
     * Helper for scroll-directions
     */
    public ScrollDirection = ScrollDirection;
    /**
     * This is the element, that should be zoomed and dragged. It is used to create the pinchZoomElement
     */
    public zoomElement!: HTMLElement;
    /**
     * The Element created with pinchZoomJs
     */
    public pinchZoomElement!: any;
    /**
     * The Container to call the touch events on
     */
    public zoomContainer!: HTMLElement;
    /**
     * The Center of the touch gestures, triggered by the button click
     */
    public touchCenter!: { x: number, y: number };
    /**
     * Bool to indicate weather the mouse is on the zoom element or not. Important for dragging via button and mouse
     * @private
     */
    private mouseEntered: boolean = false;
    /**
     * Bool to check if the touchEvent is the first event, or to check if 'touchend' has been called. For better scroll
     * @private
     */
    private isFirstAction: boolean = true;
    /**
     * The timeoutID
     * @private
     */
    private timeOutID!: number | null;
    /**
     * Save the last position of the touches to continue moving from this position
     * @private
     */
    private lastTouchPos: { x: number, y: number }[] = [];
    /**
     * The type of the current interaction with the zoomElement
     * @private
     */
    private interactionType: 'zoom' | 'drag' = 'zoom';

    /**
     * Method to initialize the pinchZoom at an element
     * @param elm
     * @param elmContainer
     */
    public initPinchZoom(elm: HTMLElement, elmContainer: HTMLElement) {
        this.zoomElement = elm;
        this.pinchZoomElement = new PinchZoom(elm, {
            minZoom: 1,
            maxZoom: 5,
            zoomOutFactor: 0.9,
            draggableUnzoomed: false,
            onZoomUpdate: this.onZoomUpdate
        });
        // The pinch-zoom-container is defined in the PinchZoomJs. Used to trigger events at button click
        this.zoomContainer = document.querySelector('.pinch-zoom-container') as HTMLElement;
        this.touchCenter = {
            x: (elm.clientWidth / 2),
            y: (elm.clientHeight / 2)
        };
        // Add eventListener for mouseWheel-zoom
        this.zoomElement.addEventListener('wheel', this.onWheelZoom);
        // Add eventListener for mouseDragging
        this.addMouseEvents(elmContainer);
    }

    /**
     * Method to call on zoom update event of pinch zoom
     * @param object The object that has been zoomed, so you can ge its attributes for better control
     * @private
     */
    private onZoomUpdate(object: any) {
        this.isZoomedIn = (object.zoomFactor > 1);
    }

    /**
     * Add events for wheel-zoom
     * @param event
     * @private
     */
    private onWheelZoom(event: any) {
        if (event.deltaY < 0) {
            this.zoomIn({
                x: event.pageX,
                y: event.pageY
            });
        } else {
            this.zoomOut({
                x: event.pageX,
                y: event.pageY
            });
        }
    }

    /**
     * Add mouseEvents for dragging the element
     * @param elm
     * @private
     */
    private addMouseEvents(elm: HTMLElement) {
        let mouseDrag = false;
        let lastPosition!: { x: number, y: number };
        const events = [{
            mouseEvents: ['mousedown', 'dragstart'],
            touchEvent: 'touchstart',
            method: (event: MouseEvent) => {
                // If an interaction already has been fired and the timeout is already there, delete the timeout and
                // reset to firstAction. 'touchend' will not be fired, but this is not even necessary
                if (this.interactionType !== 'drag' && this.timeOutID) {
                    this.isFirstAction = true;
                    this.interactionType = 'drag';
                    clearTimeout(this.timeOutID);
                }
                // TouchEvents on a display are mouseEvents too. So to avoid doubleTap zoomIn with one Tap, just trigger
                // mouseEvents if zoomedIn. Then a single Tab is enough to trigger doubleTab for zooming out:( Better but not perfect
                if (this.isZoomedIn && this.mouseEntered) {
                    mouseDrag = true;
                    lastPosition = {
                        x: event.pageX,
                        y: event.pageY
                    };
                    this.createTouchEvent('touchstart', [{
                        x: event.pageX,
                        y: event.pageY
                    }]);
                }
            }
        }, {
            // To cancel dragEvent just click one time anywhere on the screen
            mouseEvents: ['mousemove', 'drag'],
            method: (event: MouseEvent) => {
                if (mouseDrag) {
                    const newPos = {
                        x: event.pageX,
                        y: event.pageY
                    };
                    if (this.validateMove(lastPosition, newPos)) {
                        lastPosition = newPos;
                        this.createTouchEvent('touchmove', [{
                            x: event.pageX,
                            y: event.pageY
                        }]);
                    }
                }
            }
        }, {
            mouseEvents: ['mouseup', 'dragend'],
            method: () => {
                mouseDrag = false;
                this.createTouchEvent('touchend', []);
            }
        }];
        for (const listener of events) {
            for (const eventType of listener.mouseEvents) {
                // don't create new eventListener, use eventListener Methods...?
                // elm[eventType] = (event: any) => {
                //     console.log('Event: ', eventType);
                //     listener.method(event);
                // };
                elm.addEventListener(eventType, (event: any) => {
                    listener.method(event);
                });
            }
        }
        // If mouse hovers over any element that is over the zoomElement, change the mouseEntered prop
        this.zoomElement.addEventListener('mouseenter', () => {
            this.mouseEntered = true;
        });
        this.zoomElement.addEventListener('mouseleave', () => {
            this.mouseEntered = false;
        });
    }

    /**
     * Method to avoid 'jumping' of the element if dragend is fired. If so, the coordinates of the drag event are X: 0, y: 0
     * @param lastPosition
     * @param newPosition
     * @private
     */
    private validateMove(lastPosition: { x: number, y: number }, newPosition: { x: number, y: number }): boolean {
        return this.getDistance(lastPosition.x, newPosition.x) < VALIDATE_MOVE_RANGE &&
            this.getDistance(lastPosition.y, newPosition.y) < VALIDATE_MOVE_RANGE;
    }

    /**
     * Get the distance between two points in px
     * @param pos1
     * @param pos2
     * @private
     */
    private getDistance(pos1: number, pos2: number): number {
        return Math.abs(Math.abs(pos1) - Math.abs(pos2));
    }

    /**
     * Triggers zoom Event on button click
     * @param type The type of the zoom. Could be 'in' or 'out'
     */
    public onZoomClicked(type: 'in'| 'out') {
        // Update the center of the touch events in case of zooming with mouseWheel before
        this.touchCenter = {
            x: (this.zoomElement.clientWidth / 2),
            y: (this.zoomElement.clientHeight / 2)
        };
        if (type === 'in') {
            this.zoomIn(this.touchCenter);
        } else if (type === 'out') {
            this.zoomOut(this.touchCenter);
        }
    }

    /**
     * Method to zoom in
     * @param zoomCoords Coordinates where to zoom in
     * @private
     */
    private zoomIn(zoomCoords: { x: number, y: number }) {
        this.triggerTouchEvents([
            {
                x: {
                    pos: zoomCoords.x + SCROLL_OFFSET,
                    direction: 1
                },
                y: {
                    pos: zoomCoords.y,
                    direction: 0
                }
            },
            {
                x: {
                    pos: zoomCoords.x - SCROLL_OFFSET,
                    direction: -1
                },
                y: {
                    pos: zoomCoords.y,
                    direction: 0
                }
            }
        ]);
    }

    /**
     * Method to zoom out
     * @param zoomCoords The coordinates to where to zoom
     * @private
     */
    private zoomOut(zoomCoords: { x: number, y: number }) {
        if (!this.isZoomedIn) {
            // Cannot zoom out if it is not zoomed in
            return;
        }
        this.triggerTouchEvents([
            {
                x: {
                    pos: zoomCoords.x + SCROLL_OFFSET + SCROLL_MOVE_DISTANCE,
                    direction: -1
                },
                y: {
                    pos: zoomCoords.y,
                    direction: 0
                }
            },
            {
                x: {
                    pos: zoomCoords.x - (SCROLL_OFFSET + SCROLL_MOVE_DISTANCE),
                    direction: 1
                },
                y: {
                    pos: zoomCoords.y,
                    direction: 0
                }
            }
        ]);
    }

    /**
     * This method resets the zoom. It uses the doubleTap function of the pinchZoomJs
     */
    public zoomResetClicked() {
        if (!this.isZoomedIn) {
            return;
        }
        // Use this to do it consistent
        while (this.isZoomedIn) {
            this.onZoomClicked('out');
        }
    }

    /**
     * Scroll to defined directions with dragEvents
     * @param xDirection
     * @param yDirection
     */
    public scrollPage(xDirection: 1 | 0 | -1, yDirection: 1 | 0 | -1) {
        // Can cause doubleTab event if clicked too fast
        this.triggerTouchEvents([
            {
                x: {
                    pos: this.touchCenter.x,
                    direction: xDirection
                },
                y: {
                    pos: this.touchCenter.x,
                    direction: yDirection
                }
            }
        ]);
    }

    /**
     * Method to trigger Touch events with specified amount of (moving) 'fingers'
     * @param touches The coordinates of the touches. They have a position and a direction. One finger means 'drag', two
     * fingers means 'zoom'. Everything else is 'invalid'. If coordinates of the touches have only '0' directions, the
     * touch event is interpreted as a tap
     * @private
     * The idea is, not to call the complete cycle of touchstart, -move and -end every time, if zooming too fast(you
     * can see the first method at the end of this file). It means that the user don't remove his/her fingers from the
     * screen and zooms further. So:
     * Call touchstart, only if touchend has been called(or at the beginning)
     * Call touchmove while touchend has not been called
     * Call touchend after a timeout. Clear this timeout, if touchmove has been called
     */
    private triggerTouchEvents(touches: { x: Coordinates, y: Coordinates }[]) {
        if (touches.length === 1) {
            this.interactionType = 'drag';
        } else if (touches.length === 2) {
            this.interactionType = 'zoom';
        } else {
            return;
        }
        // Can be a tap of one or two fingers
        let justTap = true;
        for (const touch of touches) {
            if (touch.x.direction !== 0 || touch.y.direction !== 0) {
                justTap = false;
            }
        }
        // Trigger touch start and touch move
        // 'touchstart' to set the firstMove of PinchZoomJs to true, so the zoom and drag events can be called
        if (this.isFirstAction) {
            this.createTouchEvent('touchstart', touches.map((touch) => {
                return {
                    x: touch.x.pos,
                    y: touch.y.pos
                };
            }));
            this.lastTouchPos = [];
        }
        // If tab, dont trigger move event
        if (!justTap) {
            if (this.timeOutID) {
                // clearTimeout if moving
                clearTimeout(this.timeOutID);
            }
            // The touchDistance(per 'finger') could be a param
            const touchDistance = this.interactionType === 'zoom' ? {
                    x: SCROLL_MOVE_DISTANCE,
                    y: SCROLL_MOVE_DISTANCE
                }
                : {
                    x: DRAG_MOVE_DISTANCE,
                    y: DRAG_MOVE_DISTANCE
                };
            // eventCount needs a minimum of 4 at zooming, because the first zooming actions are not precise enough and they
            // are blocked from pinchZoomJs. Changing the eventCount cause the scaleFactor to be a bit different for zooming,
            // because its not linear. For both actions there could be the same number. Increasing the number does not change
            // the dragging, because this is linear. Could be more but... performance :D
            let eventCount = 1;
            if (this.interactionType === 'zoom') {
                eventCount = this.isFirstAction ? ZOOM_EVENTS + 4 : ZOOM_EVENTS;
            }
            const stepSize: { x: number, y: number } = {
                x: touchDistance.x / eventCount,
                y: touchDistance.y / eventCount
            };
            // The first call (+1) is for the start() method of the specified event. The eventCount tells, how often the event should be fired
            // if zooming fast, continue moving the fingers at the lastTouchPos
            for (let i = 0; i < (eventCount + 1); i++) {
                this.createTouchEvent('touchmove', touches.map((touch, index) => {
                    touch.x.pos = (stepSize.x * touch.x.direction) + (this.isFirstAction ? touch.x.pos : this.lastTouchPos[index].x);
                    touch.y.pos = (stepSize.y * touch.y.direction) + (this.isFirstAction ? touch.y.pos : this.lastTouchPos[index].y);
                    this.lastTouchPos[index] = {
                        x: touch.x.pos,
                        y: touch.y.pos
                    };
                    return this.lastTouchPos[index];
                }));
            }
            this.isFirstAction = false;
        }
        // Set a timeout, so it can be continued with zooming, if zooming fast and not trigger the complete cycle of touch
        this.timeOutID = window.setTimeout(() => {
            // trigger 'touchend' to reset the pinchZoom interaction to null. This is not really necessary but in a real
            // touch gesture it will happen every time
            this.createTouchEvent('touchend', []);
            this.isFirstAction = true;
            // This section is to prevent a drag event to be interpreted as a double tap. 0 or 1 Finger(s) dont change the
            // timer of doubleTap detection. 2 fingers means zoom, so to avoid bugs use 3. Otherwise create a merge request
            // for PinchZoomJs and insert something like:
            // 'if (enableDoubleTap){detectDoubleTap(event);}' in EventListener 'touchstart' and
            // 'enableDoubleTap: false' in the PinchZoomOptions/defaults...
            // but this works for now
            if (this.interactionType === 'drag' && !justTap) {
                const triple = [...touches];
                // drag means length 0f 1 so add 2 times the touch
                triple.push(touches[0], touches[0]);
                this.createTouchEvent('touchstart', triple.map((touch) => {
                    return {
                        x: touch.x.pos,
                        y: touch.y.pos
                    };
                }));
            }
            // with little timeout zooming works fine... otherwise the picture could jump
        }, this.interactionType === 'zoom' ? 100 : 0);
        this.bugFixBlurry();
    }

    /**
     * Creates an event that simulates a touchEvent (TouchEvents are not supported for MAC-Safari) and dispatches it
     * @param type Type of the simulated touchEvent. One of 'touchstart', 'touchmove' or 'touchend'
     * @param touches The coordinates where the touches(fingers) should be
     * @private
     */
    private createTouchEvent(type: 'touchstart' | 'touchmove' | 'touchend', touches: { x: number, y: number }[]) {
        const touchEvent: any = new CustomEvent(type);
        touchEvent.touches = this.createTouches(touches.map((coordinates) => {
            return {
                x: coordinates.x,
                y: coordinates.y
            };
        }));
        this.zoomContainer.dispatchEvent(touchEvent);
    }

    /**
     * Create Touches with specified number of 'fingers' for touchEvents, to simulate touchEvents on button click
     * @param touchCoords The coordinates of the touches
     * @private
     */
    private createTouches(touchCoords: { x: number, y: number }[]) {
        return touchCoords.map((touch, index) => {
            return {
                identifier: index,
                target: this.zoomContainer,
                pageX: touch.x,
                pageY: touch.y
            };
        });
    }

    /**
     * Move the element to the clicked direction
     * @param direction
     */
    public scrollButtonClicked(direction: ScrollDirection) {
        switch (direction) {
            case ScrollDirection.TOP:
                this.scrollPage(0, 1);
                break;
            case ScrollDirection.TOPLEFT:
                this.scrollPage(1, 1);
                break;
            case ScrollDirection.TOPRIGHT:
                this.scrollPage(-1, 1);
                break;
            case ScrollDirection.RIGHT:
                this.scrollPage(-1, 0);
                break;
            case ScrollDirection.DOWN:
                this.scrollPage(0, -1);
                break;
            case ScrollDirection.DOWNLEFT:
                this.scrollPage(1, -1);
                break;
            case ScrollDirection.DOWNRIGHT:
                this.scrollPage(-1, -1);
                break;
            case ScrollDirection.LEFT:
                this.scrollPage(1, 0);
                break;
        }
    }

    /**
     * Resets the position of the zoomElement to default 0 and scale to 1 with a little delay. Is used when the page changes
     */
    public resetPosition() {
        // Because of draggableUnzoomed = false, it's not possible to drag the container to default if zoomed out.
        // So use this instead. Do it on nextTick
        this.$nextTick(() => {
            this.zoomElement.style.transform = 'translate(' + 0 + 'px,' + 0 + 'px)' +
                'scale(' + 1 + ', ' + 1 + ')'; // scale and change the image position for a zoom effect
        });
    }

    /**
     * Method for fixing a bug, where the text gets blurry
     * @private
     */
    private bugFixBlurry() {
        // --
        // Bug fix for blurry text bug:
        // https://stackoverflow.com/questions/14677490/blurry-text-after-using-css-transform-scale-in-chrome
        // Only when backface-visibility is changed on runtime the text gets rerendered and turns crispy. If
        // the backface-visibility applied from the beginning, nothing changed.
        //
        // I applied this hack only to the zooming via controls, because for the zooming via gesture (see above in code)
        // this problem does not exist. The controls are especially for an old device that we support and this bug
        // does not appear in recent versions.
        // --
        const pageImages = this.zoomElement.querySelectorAll('.page-image');
        pageImages.forEach((item: any) => {
            item.style['backface-visibility'] = 'unset';
        });
        setTimeout(() => {
            pageImages.forEach((item: any) => {
                item.style['backface-visibility'] = 'hidden';
            });
        }, 250);
    }
}
